import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {toast} from 'react-toastify';
import {DragDropContext, Droppable, DropResult} from '@hello-pangea/dnd';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useRules} from '../../hooks/rulesHook';
import DeleteSlideModal from '../../pages/PresEditPage/components/DeleteSlideModal';
import Button from '../../../incase-domain/components/button/Button';
import {DROPDOWN_MENU_COMPACT_SUBMENU_HOVER_OPEN_CLOSE_MS} from '../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';
import AddNewSlideWrapper from '../../../components/slides/AddNewSlideWrapper';
import {swapSelectedSlidesAction} from '../../../store/actions/edit';
import {selectedPresentationSlidesSelector} from '../../../store/selectors/presentation.selectors';
import {selectedSlideIdSelector} from '../../../store/selectors/slide.selectors';
import SlidePreviewCard from './SlidePreviewCard';

function NewSlideFooterButton(): JSX.Element {
    const {t} = useTranslation();

    return (
        <AddNewSlideWrapper
            fixed="top"
            openOnMouseEnter
            closeOnMouseExit
            closeOnMouseExitDelayMs={DROPDOWN_MENU_COMPACT_SUBMENU_HOVER_OPEN_CLOSE_MS}
        >
            <Button className="c-slidePreviewFooter__footer_button" size="lg" variant="ghost" leading="ph:plus" fullWidth color="secondary">
                {t('presEditPage.newSlide')}
            </Button>
        </AddNewSlideWrapper>
    );
}

const SlidePreviewList = React.memo(function SlidePreviewList(): JSX.Element {
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const slides = useSelector(selectedPresentationSlidesSelector);
    const selectedSlideId = useSelector(selectedSlideIdSelector);

    const {checkIfSlideIsUsedInAppliedRules} = useRules();
    const [multiSelectedIds, setMultiSelectedIds] = useState<string[]>([]);
    const [deleteModal, setDeleteModal] = useState(false);
    const anchorIdRef = useRef<string | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (multiSelectedIds.length === 0) return undefined;
        const onMouseDown = (e: MouseEvent): void => {
            const target = e.target as HTMLElement | null;
            if (!target) return;
            if (containerRef.current?.contains(target)) return;
            if (target.closest?.('.contexify, [role="dialog"], .c-confirmModal')) return;
            setMultiSelectedIds([]);
        };
        document.addEventListener('mousedown', onMouseDown);
        return () => document.removeEventListener('mousedown', onMouseDown);
    }, [multiSelectedIds.length]);

    useEffect(() => {
        setMultiSelectedIds((prev) => {
            const next = prev.filter((id) => slides.some((s) => s._id === id));
            return next.length === prev.length ? prev : next;
        });
    }, [slides]);

    const multiSelectedSlides = useMemo(() => slides.filter((s) => multiSelectedIds.includes(s._id)), [slides, multiSelectedIds]);

    const handlePlainClick = useCallback((slideId: string): void => {
        anchorIdRef.current = slideId;
        setMultiSelectedIds((prev) => (prev.length ? [] : prev));
    }, []);

    const handleModifierClick = useCallback(
        (slideId: string, e: React.MouseEvent): void => {
            const anchorId = anchorIdRef.current ?? selectedSlideId;
            if (e.shiftKey) {
                const from = slides.findIndex((s) => s._id === anchorId);
                const to = slides.findIndex((s) => s._id === slideId);
                if (to === -1) return;
                const [start, end] = from === -1 ? [to, to] : [Math.min(from, to), Math.max(from, to)];
                setMultiSelectedIds(slides.slice(start, end + 1).map((s) => s._id));
                return;
            }
            anchorIdRef.current = slideId;
            setMultiSelectedIds((prev) => {
                const base = prev.length ? prev : [selectedSlideId];
                const next = base.includes(slideId) ? base.filter((id) => id !== slideId) : [...base, slideId];
                return next;
            });
        },
        [slides, selectedSlideId]
    );

    const canBatchDelete = multiSelectedSlides.length > 1 && multiSelectedSlides.length < slides.length;

    useEffect(() => {
        if (multiSelectedIds.length === 0) return undefined;
        const onKeyDown = (e: KeyboardEvent): void => {
            const target = e.target as HTMLElement | null;
            if (target && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))) return;
            if (e.key === 'Escape') {
                setMultiSelectedIds([]);
            } else if ((e.key === 'Delete' || e.key === 'Backspace') && canBatchDelete) {
                e.preventDefault();
                if (multiSelectedSlides.some((s) => checkIfSlideIsUsedInAppliedRules(s))) {
                    toast.error(t('toasts.deleteSlideWithAppliedRuleError'));
                    return;
                }
                setDeleteModal(true);
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [multiSelectedIds.length, canBatchDelete, multiSelectedSlides, checkIfSlideIsUsedInAppliedRules, t]);

    const swapSlideOrderHandler = useCallback(
        (newIndex: number, oldIndex: number): void => {
            dispatch(swapSelectedSlidesAction(newIndex, oldIndex));
        },
        [dispatch]
    );

    const onDragEnd = useCallback(
        (e: DropResult): void => {
            if (typeof e.destination?.index !== 'undefined') {
                swapSlideOrderHandler(e.destination.index, e.source.index);
            }
        },
        [swapSlideOrderHandler]
    );

    return (
        <div ref={containerRef} className="swiper c-slidePreviewFooter__body">
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="droppable" direction="vertical">
                    {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps} className="c-slidePreviewFooter__body_wrapper">
                            {slides.map((slide, index) => (
                                <div key={slide._id}>
                                    <SlidePreviewCard
                                        index={index}
                                        displayedSlide={slide}
                                        selectedSlideId={selectedSlideId}
                                        isMultiSelected={multiSelectedIds.includes(slide._id)}
                                        multiSelectedSlides={multiSelectedSlides}
                                        onModifierClick={handleModifierClick}
                                        onPlainClick={handlePlainClick}
                                    />
                                </div>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
            {deleteModal && multiSelectedSlides.length > 1 && (
                <DeleteSlideModal
                    deleteModalOpen={deleteModal}
                    setDeleteModalOpen={(open) => {
                        setDeleteModal(open);
                        if (!open) setMultiSelectedIds([]);
                    }}
                    slides={multiSelectedSlides}
                />
            )}
        </div>
    );
});

function SlidePreviewFooter(): JSX.Element {
    return (
        <div className="c-slidePreviewFooter">
            <SlidePreviewList />
            <div className="c-slidePreviewFooter__footer">
                <NewSlideFooterButton />
            </div>
        </div>
    );
}

export default React.memo(SlidePreviewFooter);
